/**
 * Initializes horizontal scroll arrows and overflow behavior for a scroll container root element.
 *
 * @param {HTMLElement} rootElement - Element containing .horizontal-scroll-container
 */
function initializeHorizontalScrollContainer(rootElement) {
    if (rootElement == null) {
        return;
    }

    if (rootElement.dataset.horizontalScrollInitialized === 'true') {
        scheduleHorizontalScrollContainerRefresh(rootElement);
        return;
    }

    const horizontalScrollContainer = rootElement.querySelector('.horizontal-scroll-container');
    const scrollContainer = rootElement.querySelector('.horizontal-scroll-container__items');
    const leftArrowContainer = rootElement.querySelector('.horizontal-scroll-container__left-arrow');
    const rightArrowContainer = rootElement.querySelector('.horizontal-scroll-container__right-arrow');
    const leftArrowButton = leftArrowContainer ? leftArrowContainer.querySelector('button') : null;
    const rightArrowButton = rightArrowContainer ? rightArrowContainer.querySelector('button') : null;
    const disableArrowsAtEdges =
        horizontalScrollContainer != null &&
        horizontalScrollContainer.classList.contains('horizontal-scroll-container--disabled-arrows-at-edges');

    if (scrollContainer == null) {
        return;
    }

    /**
     * @param {HTMLElement} arrowContainer
     * @param {HTMLButtonElement|null} arrowButton
     * @param {boolean} disabled
     */
    function setArrowDisabled(arrowContainer, arrowButton, disabled) {
        if (arrowButton == null) {
            return;
        }

        arrowButton.disabled = disabled;
        arrowContainer.classList.toggle('is-disabled', disabled);
    }

    function updateArrowVisibility() {
        if (leftArrowContainer == null || rightArrowContainer == null) {
            return;
        }

        const scrollLeft = scrollContainer.scrollLeft;
        const scrollWidth = scrollContainer.scrollWidth;
        const clientWidth = scrollContainer.clientWidth;
        const canScroll = scrollWidth > clientWidth;
        const isAtStart = scrollLeft <= 1;
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

        if (!canScroll) {
            leftArrowContainer.style.display = 'none';
            rightArrowContainer.style.display = 'none';
            return;
        }

        if (disableArrowsAtEdges) {
            leftArrowContainer.style.display = '';
            rightArrowContainer.style.display = '';
            setArrowDisabled(leftArrowContainer, leftArrowButton, isAtStart);
            setArrowDisabled(rightArrowContainer, rightArrowButton, isAtEnd);
            return;
        }

        setArrowDisabled(leftArrowContainer, leftArrowButton, false);
        setArrowDisabled(rightArrowContainer, rightArrowButton, false);
        leftArrowContainer.style.display = isAtStart ? 'none' : '';
        rightArrowContainer.style.display = isAtEnd ? 'none' : '';
    }

    function handleArrowClick(event) {
        if (event.currentTarget.disabled) {
            return;
        }

        event.preventDefault();
        const currentScrollLeft = scrollContainer.scrollLeft;
        const scrollAmount = 200;

        if (event.currentTarget === leftArrowButton) {
            scrollContainer.scrollTo({
                left: Math.max(0, currentScrollLeft - scrollAmount),
                behavior: 'smooth',
            });
        } else if (event.currentTarget === rightArrowButton) {
            scrollContainer.scrollTo({
                left: currentScrollLeft + scrollAmount,
                behavior: 'smooth',
            });
        }
    }

    if (leftArrowButton != null) {
        leftArrowButton.addEventListener('click', handleArrowClick);
    }
    if (rightArrowButton != null) {
        rightArrowButton.addEventListener('click', handleArrowClick);
    }

    scrollContainer.addEventListener('scroll', updateArrowVisibility, { passive: true });
    window.addEventListener('resize', updateArrowVisibility, { passive: true });

    rootElement._horizontalScrollRefresh = updateArrowVisibility;
    rootElement.dataset.horizontalScrollInitialized = 'true';
    scheduleHorizontalScrollContainerRefresh(rootElement);
}

/**
 * Initializes scroll arrows/overflow and roving-tabindex keyboard navigation.
 *
 * Runs scroll init first, then wires keyboard when `itemSelector` matches tabbable items.
 * Use this for horizontal lists of buttons or links. For non-list content, use
 * `initializeHorizontalScrollContainer` instead.
 *
 * @param {HTMLElement} rootElement - Element containing .horizontal-scroll-container
 * @param {Object} keyboardOptions
 * @param {string} keyboardOptions.itemSelector - Focusable items inside `.horizontal-scroll-container__items`
 * @param {() => (HTMLElement|null)} keyboardOptions.getActiveItem - Item that receives the single tab stop
 * @param {(item: HTMLElement, event: KeyboardEvent) => void} [keyboardOptions.onActivate] - Enter/Space handler for non-native controls; omit for `<button>` / `<a>`
 */
function initializeHorizontalScrollContainerWithKeyboard(rootElement, keyboardOptions) {
    initializeHorizontalScrollContainer(rootElement);
    setupHorizontalScrollContainerKeyboard(rootElement, keyboardOptions);
}

/**
 * Re-runs arrow visibility after layout changes (e.g. when a hidden container is revealed).
 *
 * @param {HTMLElement} rootElement - Element passed to initializeHorizontalScrollContainer
 */
function refreshHorizontalScrollContainer(rootElement) {
    if (rootElement?._horizontalScrollRefresh != null) {
        rootElement._horizontalScrollRefresh();
    }
}

function scheduleHorizontalScrollContainerRefresh(rootElement) {
    requestAnimationFrame(function () {
        requestAnimationFrame(function () {
            refreshHorizontalScrollContainer(rootElement);
        });
    });
}

/**
 * Scrolls an item into view within a horizontal scroll container.
 *
 * @param {HTMLElement} rootElement - Element containing .horizontal-scroll-container
 * @param {HTMLElement} itemElement - The item to scroll into view
 */
function scrollHorizontalScrollContainerItemIntoView(rootElement, itemElement) {
    if (rootElement == null || itemElement == null) {
        return;
    }

    const scrollContainer = rootElement.querySelector('.horizontal-scroll-container__items');
    if (scrollContainer == null) {
        return;
    }

    const isMobile = document.getElementsByTagName('body')[0].classList.contains('mobile-page');

    if (isMobile) {
        itemElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        refreshHorizontalScrollContainer(rootElement);
        return;
    }

    const itemRect = itemElement.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();

    if (itemRect.left < containerRect.left + 100) {
        itemElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    } else if (itemRect.right > containerRect.right - 100) {
        itemElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
    }
    refreshHorizontalScrollContainer(rootElement);
}

/**
 * Re-applies roving tabindex after the active item changes programmatically
 * (e.g. embed floor.change). Does not move focus unless the user tabs into the container.
 *
 * @param {HTMLElement} rootElement - Element initialized with initializeHorizontalScrollContainerWithKeyboard
 */
function refreshHorizontalScrollContainerKeyboard(rootElement) {
    if (rootElement?._horizontalScrollKeyboardRefresh != null) {
        rootElement._horizontalScrollKeyboardRefresh();
    }
}

// ============================================================================
// KEYBOARD NAVIGATION (internal)
// ============================================================================

/**
 * @param {HTMLElement} rootElement
 * @param {Object} options
 * @param {string} options.itemSelector
 * @param {() => (HTMLElement|null)} options.getActiveItem
 * @param {(item: HTMLElement, event: KeyboardEvent) => void} [options.onActivate]
 */
function setupHorizontalScrollContainerKeyboard(rootElement, options) {
    if (rootElement == null || options == null) {
        return;
    }

    if (rootElement.dataset.horizontalScrollKeyboardInitialized === 'true') {
        refreshHorizontalScrollContainerKeyboard(rootElement);
        return;
    }

    const scrollContainer = rootElement.querySelector('.horizontal-scroll-container__items');
    if (scrollContainer == null) {
        return;
    }

    const items = Array.from(scrollContainer.querySelectorAll(options.itemSelector));
    if (items.length === 0) {
        // No tabbable list yet; scroll init still applies.
        return;
    }

    const getActiveItem = options.getActiveItem;
    const onActivate = options.onActivate;

    /**
     * Roving tabindex: one tab stop into the scroller, arrow keys move among items.
     * Only the active/selected item is tabbable; others are removed from tab order.
     */
    function updateTabIndexes() {
        const activeItem = getActiveItem() ?? items[0];

        items.forEach(function (item) {
            item.setAttribute('tabindex', item === activeItem ? '0' : '-1');
        });
    }

    /**
     * @param {HTMLElement} item
     */
    function focusItem(item) {
        if (item == null) {
            return;
        }
        // preventScroll: horizontal scroll is handled by scrollHorizontalScrollContainerItemIntoView
        item.focus({ preventScroll: true });
        scrollHorizontalScrollContainerItemIntoView(rootElement, item);
    }

    /**
     * Arrow keys move focus only (they do not activate/select). Activation is left
     * to native button/link behavior or the optional onActivate callback.
     *
     * @param {KeyboardEvent} event
     */
    function handleKeyboardNavigation(event) {
        if (!scrollContainer.contains(document.activeElement)) {
            return;
        }

        const activeItem = document.activeElement;
        const currentIndex = items.indexOf(activeItem);
        if (currentIndex === -1) {
            return;
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (currentIndex > 0) {
                focusItem(items[currentIndex - 1]);
            }
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            if (currentIndex < items.length - 1) {
                focusItem(items[currentIndex + 1]);
            }
        } else if (onActivate != null && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onActivate(activeItem, event);
        }
    }

    /**
     * When the user tabs into the items container, redirect focus to the active item
     * so keyboard users always enter on the current selection.
     *
     * @param {FocusEvent} event
     */
    function handleItemsContainerFocus(event) {
        if (event.target !== scrollContainer) {
            return;
        }

        const activeItem = getActiveItem() ?? items[0];
        if (activeItem != null) {
            event.preventDefault();
            focusItem(activeItem);
        }
    }

    scrollContainer.addEventListener('focusin', handleItemsContainerFocus);
    scrollContainer.addEventListener('keydown', handleKeyboardNavigation);

    rootElement._horizontalScrollKeyboardRefresh = updateTabIndexes;
    rootElement.dataset.horizontalScrollKeyboardInitialized = 'true';
    updateTabIndexes();
}
